import {getClient, getCurrentScope, type Integration} from "@sentry/core";
import {diagnosticIdentifier, recordDiagnostic, reportError, reportWarning} from "./observability.js";

/** The website and embedded shell have no authenticated frontend diagnostics lifecycle. */
export function initializeBrowserDiagnostics(service: "website" | "chatgpt"): void {
  const key = `sprout.diagnostics.${service}.session`;
  let sessionId: string = crypto.randomUUID();
  let storageError: unknown;
  try {
    sessionId = diagnosticIdentifier(window.sessionStorage.getItem(key)) ?? sessionId;
    window.sessionStorage.setItem(key, sessionId);
  } catch (error) {
    storageError = error;
  }
  getCurrentScope().setTags({sessionId, screen: window.location.pathname, platform: "web", online: navigator.onLine});
  if (storageError) reportWarning(storageError, "storage.session");
  recordDiagnostic("app.started");
}

/** Ad blockers routinely stop analytics tags, including the Cloudflare Web Analytics beacon the edge injects; those failures are expected, not app faults. */
const analyticsHosts = ["googletagmanager.com", "google-analytics.com", "googleadservices.com", "doubleclick.net", "connect.facebook.net", "analytics.tiktok.com", "clarity.ms", "posthog.com", "p.mindcrafters.group", "cloudflareinsights.com"];
/** A Google tag or Tag Manager container served first-party through a tag gateway path (`/metrics/?id=G-…`). */
const gatewayTag = /[?&]id=(G|GT|GTM|AW|DC)-/;

function isAnalyticsResource(element: Element): boolean {
  const source = element.getAttribute("src") ?? element.getAttribute("href");
  if (!source) return false;
  try {
    const url = new URL(source, window.location.href);
    return analyticsHosts.some((domain) => url.hostname === domain || url.hostname.endsWith(`.${domain}`)) || gatewayTag.test(url.search);
  } catch {
    return false;
  }
}

const mediaTypes = ["img", "audio", "video", "source"];

/**
 * Separates a request the browser never sent (no timing entry: blocked images,
 * extensions, aborted prerender) from one the network or server failed.
 */
function resourceFailureAttributes(element: Element): {resourceStatus: string | number; resourceBytes?: number; automated: boolean; readyState: string; pageAgeMs: number} {
  const source = element.getAttribute("src") ?? element.getAttribute("href");
  let entry: PerformanceResourceTiming | undefined;
  try {
    if (source) entry = performance.getEntriesByName(new URL(source, window.location.href).href, "resource").at(-1) as PerformanceResourceTiming | undefined;
  } catch {
    // Relative sources always resolve; malformed ones simply have no timing entry.
  }
  return {
    resourceStatus: entry ? (entry.responseStatus ?? "unknown") : "none",
    ...(entry ? {resourceBytes: entry.transferSize} : {}),
    automated: navigator.webdriver === true,
    readyState: document.readyState,
    pageAgeMs: Math.round(performance.now())
  };
}

/**
 * Resource failures do not reach window.onerror or unhandledrejection.
 * `handledViolation` marks violations the page already recovers from; they are
 * logged as diagnostics instead of filed as errors.
 */
export function browserFailuresIntegration(handledViolation?: (event: SecurityPolicyViolationEvent) => boolean): Integration {
  return {
    name: "SproutBrowserFailures",
    setup(client) {
      if (typeof window === "undefined") return;
      const onResourceError = (event: globalThis.Event) => {
        if (getClient() !== client || !(event.target instanceof Element) || isAnalyticsResource(event.target)) return;
        const resourceType = event.target.tagName.toLowerCase();
        if (["script", "link", "img", "iframe", "audio", "video", "source"].includes(resourceType)) {
          const attributes = {resourceType, resourceUrl: event.target.getAttribute("src") ?? event.target.getAttribute("href") ?? "unknown", ...resourceFailureAttributes(event.target)};
          // Media the server never answered was blocked or aborted on the client (images off, extensions, a cancelled load); scripts, styles, and HTTP errors still file.
          if (mediaTypes.includes(resourceType) && (attributes.resourceStatus === "none" || attributes.resourceStatus === 0)) recordDiagnostic("browser.resource.unanswered", attributes);
          else reportError(new Error("Resource failed to load"), "browser.resource", attributes);
        }
      };
      const onPreloadError = (event: globalThis.Event) => {
        if (getClient() !== client) return;
        // A page that already reloads to fetch the current release has recovered.
        if (event.defaultPrevented) recordDiagnostic("browser.preload.recovered");
        else reportError("payload" in event ? event.payload : undefined, "browser.preload");
      };
      const onPolicyViolation = (event: SecurityPolicyViolationEvent) => {
        if (getClient() !== client) return;
        // Reporting a blocked telemetry request would recursively trigger another violation.
        try {
          if (new URL(event.blockedURI).hostname === client.getDsn()?.host) return;
        } catch {
          // CSP also uses non-URL values such as "inline" and "eval".
        }
        const attributes = {
          directive: event.effectiveDirective,
          disposition: event.disposition,
          resourceUrl: event.blockedURI,
          url: event.sourceFile,
          line: event.lineNumber,
          column: event.columnNumber,
          status: event.statusCode
        };
        if (handledViolation?.(event)) recordDiagnostic("browser.csp.handled", attributes);
        else (event.disposition === "report" ? reportWarning : reportError)(new Error("Content security policy violation"), "browser.csp", attributes);
      };
      const onClick = (event: globalThis.Event) => {
        if (getClient() !== client || !(event.target instanceof Element)) return;
        const control = event.target.closest("button,a,[role='button']");
        if (!control) return;
        recordDiagnostic("ui.click", {
          element: control.tagName.toLowerCase(),
          control: diagnosticIdentifier(control.getAttribute("data-testid") ?? control.getAttribute("data-slot") ?? control.id) ?? "unnamed"
        });
      };
      const flush = () => {
        if (getClient() === client) void client.flush(2000);
      };
      const onVisibility = () => {
        if (getClient() !== client) return;
        recordDiagnostic("app.visibility", {visibility: document.visibilityState, online: navigator.onLine});
        if (document.visibilityState === "hidden") flush();
      };
      const onNetwork = () => {
        if (getClient() !== client) return;
        getCurrentScope().setTag("online", navigator.onLine);
        recordDiagnostic("app.network", {online: navigator.onLine});
      };
      window.addEventListener("error", onResourceError, true);
      window.addEventListener("vite:preloadError", onPreloadError);
      document.addEventListener("securitypolicyviolation", onPolicyViolation);
      window.addEventListener("online", onNetwork);
      window.addEventListener("offline", onNetwork);
      window.addEventListener("click", onClick, true);
      window.addEventListener("pagehide", flush);
      document.addEventListener("visibilitychange", onVisibility);
      const cleanup = () => {
        window.removeEventListener("error", onResourceError, true);
        window.removeEventListener("vite:preloadError", onPreloadError);
        document.removeEventListener("securitypolicyviolation", onPolicyViolation);
        window.removeEventListener("online", onNetwork);
        window.removeEventListener("offline", onNetwork);
        window.removeEventListener("click", onClick, true);
        window.removeEventListener("pagehide", flush);
        document.removeEventListener("visibilitychange", onVisibility);
      };
      client.on("close", cleanup);
      client.registerCleanup(cleanup);
    }
  };
}
